/**
 * Códigos do cadastro de produto — funções PURAS (GTIN/GS1, NCM, CEST, FCI,
 * SKU novo). Espelho do `backend_v2/src/modules/products/productCodes.js`: os
 * CÓDIGOS de erro são os mesmos do servidor (servidor = cliente, spec B.11),
 * então o front pode acusar antes do POST/PUT e o 400 do servidor cai no mesmo
 * campo com o mesmo texto.
 *
 * Nada aqui toca rede nem React; testado em `utils/__tests__/productCodes.test.mjs`.
 */

import {
    FCI_PATTERN,
    GTIN_LENGTHS,
    PRODUCT_SKU_MAX,
    PRODUCT_SKU_PATTERN,
    PRODUCT_SKU_RESERVED_PREFIX,
} from '../constants/productCatalog';

/** Só os dígitos de qualquer entrada ("789 8645-22" → "789864522"). */
export function OnlyDigits(value) {
    return String(value ?? '').replace(/\D/g, '');
}

/**
 * Dígito verificador GS1 (módulo 10, pesos 3 e 1 a partir da DIREITA) para os
 * dígitos SEM o DV. Serve para GTIN-8/12/13/14. Número 0–9, ou null se o corpo
 * não for só dígitos (igual ao backend).
 */
export function GtinCheckDigit(digitsWithoutCheck) {
    const body = String(digitsWithoutCheck ?? '');
    if (!/^\d+$/.test(body)) {
        return null;
    }
    let sum = 0;
    for (let index = 0; index < body.length; index += 1) {
        const digit = Number(body[body.length - 1 - index]);
        sum += index % 2 === 0 ? digit * 3 : digit;
    }
    return (10 - (sum % 10)) % 10;
}

/**
 * Valida um GTIN. `{ ok, empty, code, length, expected }` — os `code` são os do
 * backend (`NOT_DIGITS`, `GTIN_LENGTH`, `GTIN_CHECK_DIGIT` com o DV `expected`).
 * Diferença deliberada do servidor: vazio é `{ ok: true, empty: true }` porque
 * todo GTIN do formulário é opcional (quem exige preenchimento decide).
 */
export function ValidateGtin(value, { lengths = GTIN_LENGTHS } = {}) {
    const raw = String(value ?? '').trim();
    if (!raw) {
        return { ok: true, empty: true, code: null, length: 0, expected: null };
    }
    if (!/^\d+$/.test(raw)) {
        return { ok: false, empty: false, code: 'NOT_DIGITS', length: raw.length, expected: null };
    }
    if (!lengths.includes(raw.length)) {
        return { ok: false, empty: false, code: 'GTIN_LENGTH', length: raw.length, expected: null };
    }
    const expected = GtinCheckDigit(raw.slice(0, -1));
    if (expected !== Number(raw.slice(-1))) {
        return { ok: false, empty: false, code: 'GTIN_CHECK_DIGIT', length: raw.length, expected };
    }
    return { ok: true, empty: false, code: null, length: raw.length, expected };
}

/**
 * EAN-13 contido num GTIN-14 (DUN-14): tira o indicador e o DV e recalcula.
 * `17899069400063` → `7899069400066`. Entrada que não é GTIN-14 → null.
 */
export function DeriveGtin13FromGtin14(value) {
    const digits = OnlyDigits(value);
    if (digits.length !== 14) {
        return null;
    }
    const body = digits.slice(1, 13);
    return `${body}${GtinCheckDigit(body)}`;
}

/** NCM mascarado enquanto digita: "23091000" → "2309.10.00". */
export function FormatNcm(value) {
    const digits = OnlyDigits(value).slice(0, 8);
    if (digits.length <= 4) {
        return digits;
    }
    if (digits.length <= 6) {
        return `${digits.slice(0, 4)}.${digits.slice(4)}`;
    }
    return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6)}`;
}

/** NCM: 8 dígitos (vazio é válido — quem exige decide). */
export function ValidateNcm(value) {
    const digits = OnlyDigits(value);
    if (!digits) {
        return { ok: true, empty: true, code: null };
    }
    return digits.length === 8
        ? { ok: true, empty: false, code: null }
        : { ok: false, empty: false, code: 'NCM_LENGTH' };
}

/** CEST mascarado enquanto digita: "1234567" → "12.345.67". */
export function FormatCest(value) {
    const digits = OnlyDigits(value).slice(0, 7);
    if (digits.length <= 2) {
        return digits;
    }
    if (digits.length <= 5) {
        return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    }
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
}

/** CEST: 7 dígitos (vazio é válido). */
export function ValidateCest(value) {
    const digits = OnlyDigits(value);
    if (!digits) {
        return { ok: true, empty: true, code: null };
    }
    return digits.length === 7
        ? { ok: true, empty: false, code: null }
        : { ok: false, empty: false, code: 'CEST_LENGTH' };
}

/**
 * Número da FCI normalizado como no servidor: maiúsculas, sem espaços; 32 hex
 * sem hífen ganham os hífens do padrão 8-4-4-4-12. Vazio → null.
 */
export function NormalizeFciNumber(value) {
    const text = String(value ?? '')
        .replace(/\s+/g, '')
        .toUpperCase();
    if (!text) {
        return null;
    }
    if (/^[0-9A-F]{32}$/.test(text)) {
        return `${text.slice(0, 8)}-${text.slice(8, 12)}-${text.slice(12, 16)}-${text.slice(16, 20)}-${text.slice(20)}`;
    }
    return text;
}

/** Número FCI: GUID de 36 caracteres (NT 2013.006). Vazio é válido. */
export function ValidateFci(value) {
    const fci = NormalizeFciNumber(value);
    if (!fci) {
        return { ok: true, empty: true, code: null };
    }
    return FCI_PATTERN.test(fci)
        ? { ok: true, empty: false, code: null }
        : { ok: false, empty: false, code: 'FCI_FORMAT' };
}

/**
 * Casas decimais de um número pela sua representação em texto, tratando a
 * notação científica (`1e-11` tem 11 casas). Mesma regra do backend.
 */
export function HasAtMostDecimals(value, places) {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return false;
    }
    const text = String(number).toLowerCase();
    const [mantissa, exponentText] = text.split('e');
    const fraction = mantissa.split('.')[1] || '';
    const exponent = exponentText ? Number(exponentText) : 0;
    const decimals = Math.max(0, fraction.length - exponent);
    return decimals <= places;
}

/** SKU digitado → forma gravável (trim + maiúsculas). Sem decode. */
export function NormalizeSkuInput(value) {
    return String(value ?? '')
        .trim()
        .toUpperCase();
}

/** Mensagens (pt-BR) da regra de SKU novo — as MESMAS do backend (`productCodes.js`). */
export const SKU_ERROR_MESSAGES = {
    OBRIGATORIO: 'Informe o SKU',
    CONTROLE: 'O SKU tem caractere invisível (de controle)',
    PERCENT: 'Não use % no SKU',
    VIRGULA: 'Não use vírgula no SKU (use ponto: 10.1KG)',
    ESPACO: 'Não use espaço no SKU',
    ACENTO: 'Não use acento nem Ç no SKU',
    PARENTESES: 'Não use parênteses, colchetes ou chaves no SKU',
    CARACTERE_INVALIDO: 'Use só letras, números e . _ / + - no SKU',
    INICIO_FIM: 'O SKU começa e termina com letra ou número',
    LONGO: `O SKU tem no máximo ${PRODUCT_SKU_MAX} caracteres`,
    BID_RESERVADO: `O prefixo ${PRODUCT_SKU_RESERVED_PREFIX} é reservado`,
};

/** Caractere de controle (U+0000–U+001F, U+007F): os SKUs legados decodificados têm. */
function hasControlCharacter(text) {
    for (let index = 0; index < text.length; index += 1) {
        const code = text.charCodeAt(index);
        if (code < 0x20 || code === 0x7f) {
            return true;
        }
    }
    return false;
}

function skuError(code) {
    return { code, message: SKU_ERROR_MESSAGES[code] };
}

/**
 * Regra do SKU NOVO (só no create; depois de salvo o SKU não muda):
 * trim + maiúsculas; 1–60; `PRODUCT_SKU_PATTERN`; prefixo `BID-` reservado.
 * Devolve o SKU normalizado e TODOS os erros, cada um com código próprio para
 * a tela dizer o que trocar — mesma ordem e mesmos códigos do backend.
 * `{ sku, errors: [{ code, message }] }` (vazio = válido).
 */
export function ValidateNewSku(raw) {
    const sku = NormalizeSkuInput(raw);
    if (!sku) {
        return { sku, errors: [skuError('OBRIGATORIO')] };
    }
    const errors = [];
    if (hasControlCharacter(sku)) {
        errors.push(skuError('CONTROLE'));
    }
    if (sku.includes('%')) {
        errors.push(skuError('PERCENT'));
    }
    if (sku.includes(',')) {
        errors.push(skuError('VIRGULA'));
    }
    if (/\s/.test(sku)) {
        errors.push(skuError('ESPACO'));
    }
    // Fora do ASCII (acento, Ç, símbolos tipográficos).
    if (/[\u0080-\uffff]/.test(sku)) {
        errors.push(skuError('ACENTO'));
    }
    if (/[()[\]{}]/.test(sku)) {
        errors.push(skuError('PARENTESES'));
    }
    const specific = errors.length > 0;
    // Qualquer outro caractere fora do alfabeto (#, *, &, :, ...).
    if (!specific && /[^A-Z0-9._/+-]/.test(sku)) {
        errors.push(skuError('CARACTERE_INVALIDO'));
    }
    if (/^[._/+-]|[._/+-]$/.test(sku)) {
        errors.push(skuError('INICIO_FIM'));
    }
    if (sku.length > PRODUCT_SKU_MAX) {
        errors.push(skuError('LONGO'));
    }
    if (sku.startsWith(PRODUCT_SKU_RESERVED_PREFIX)) {
        errors.push(skuError('BID_RESERVADO'));
    }
    // Rede de segurança: a regex é a regra final (nenhum caso escapa sem código).
    if (errors.length === 0 && !PRODUCT_SKU_PATTERN.test(sku)) {
        errors.push(skuError('CARACTERE_INVALIDO'));
    }
    return { sku, errors };
}

/**
 * Chave de "SKU parecido": vírgula vira ponto e somem `. - _ /`. Pega os
 * quase-duplicados reais (GPMENUFMPFA10,1KG × GPMENUFMPFA10.1KG).
 */
export function SkuSimilarityKey(value) {
    return NormalizeSkuInput(value)
        .replace(/,/g, '.')
        .replace(/[.\-_/]/g, '');
}
